import React, { type ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import Plot from 'react-plotly.js';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as styles from './office-detail-tab.styles';
import * as tabStyles from '../statistics-tabs.styles';
import DashboardTheme from '../dashboard-theme';
import plotlyChartConfig from '../components/plotly-chart-config';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../../core/styles.constants';
import { useDashboardOfficeDetail } from '../hooks/dashboard.hook';
import { useOfficeManagerOrAdminOffices } from '../../../offices/hooks/offices.hook';
import Gauge from '../components/gauge.component';
import { type ParkingOccupation, type RoomOccupation } from '../models/dashboard.models';
import { usePagedRows, useSortableRows } from '../components/table-controls.hook';
import SortableHeaderCell from '../components/sortable-header-cell.component';
import TablePagination from '../components/table-pagination.component';

const ROWS_PER_PAGE = 8;

interface Props {
  readonly selectedOfficeId?: string;
  readonly onSelectOffice: (officeId: string) => void;
  readonly dataVersion?: number;
}

const QUIET_COLOR = DashboardTheme.gray300;
const HEALTHY_COLOR = DashboardTheme.green600;
const OVER_COLOR = DashboardTheme.red500;

const pctColor = (value: number, min: number, max: number): string => {
  if (value > max) return OVER_COLOR;
  if (value < min) return QUIET_COLOR;
  return HEALTHY_COLOR;
};

const OccupancyBar = ({ percentage, min, max }: { percentage: number; min: number; max: number }): React.JSX.Element => (
  <div className={style(styles.barCell)}>
    <div className={style(styles.barTrack)}>
      <div className={style(styles.barFill(percentage, pctColor(percentage, min, max)))} />
    </div>
    <span className={style(styles.barLabel)}>{percentage.toFixed(0)}%</span>
  </div>
);

const OfficeDetailTab = ({ selectedOfficeId, onSelectOffice, dataVersion }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const { offices } = useOfficeManagerOrAdminOffices();
  const { initialized, detail, error } = useDashboardOfficeDetail(selectedOfficeId, undefined, dataVersion);

  const sortedOffices = useMemo(() => [...(offices ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [offices]);

  const {
    sortedRows: sortedRooms,
    sortKey: roomsSortKey,
    sortDirection: roomsSortDirection,
    toggleSort: toggleRoomsSort,
  } = useSortableRows<RoomOccupation>(detail?.rooms ?? [], (room, key) => {
    switch (key) {
      case 'morning':
        return room.morning.occupancyPercentage;
      case 'afternoon':
        return room.afternoon.occupancyPercentage;
      case 'checkedIn':
        return room.checkedIn;
      case 'threshold':
        return room.thresholdMinPercentage;
      case 'name':
      default:
        return room.name;
    }
  });
  const { pagedRows: pagedRooms, page: roomsPage, setPage: setRoomsPage, pageCount: roomsPageCount } = usePagedRows(
    sortedRooms,
    ROWS_PER_PAGE,
  );

  const {
    sortedRows: sortedParkings,
    sortKey: parkingsSortKey,
    sortDirection: parkingsSortDirection,
    toggleSort: toggleParkingsSort,
  } = useSortableRows<ParkingOccupation>(detail?.parkings ?? [], (parking, key) => {
    switch (key) {
      case 'morning':
        return parking.morning.occupancyPercentage;
      case 'afternoon':
        return parking.afternoon.occupancyPercentage;
      case 'threshold':
        return parking.thresholdMinPercentage;
      case 'name':
      default:
        return parking.name;
    }
  });
  const { pagedRows: pagedParkings, page: parkingsPage, setPage: setParkingsPage, pageCount: parkingsPageCount } = usePagedRows(
    sortedParkings,
    ROWS_PER_PAGE,
  );

  const onChangeOffice = useCallback(
    (event: ChangeEvent<HTMLSelectElement>): void => {
      if (event.target.value) {
        onSelectOffice(event.target.value);
      }
    },
    [onSelectOffice],
  );

  const chartData = useMemo(() => {
    const rooms = detail?.rooms ?? [];
    return [
      {
        x: rooms.map((room) => room.name),
        y: rooms.map((room) => room.morning.occupancyPercentage),
        name: t('dashboard-table-morning'),
        type: 'bar' as const,
        marker: { color: DashboardTheme.amber500 },
      },
      {
        x: rooms.map((room) => room.name),
        y: rooms.map((room) => room.afternoon.occupancyPercentage),
        name: t('dashboard-table-afternoon'),
        type: 'bar' as const,
        marker: { color: DashboardTheme.navy700 },
      },
    ];
  }, [detail, t]);

  return (
    <div>
      <div className={style(styles.selectorRow)}>
        <span className={style(styles.selectorLabel)}>{t('dashboard-office-select-placeholder')}</span>
        <select className={style(styles.officeSelect)} value={selectedOfficeId ?? ''} onChange={onChangeOffice}>
          <option value="">{t('dashboard-office-select-placeholder')}</option>
          {sortedOffices.map((office) => (
            <option key={office.id} value={office.id}>
              {office.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedOfficeId && <div className={style(styles.selectPrompt)}>{t('dashboard-office-select-prompt')}</div>}

      {selectedOfficeId && !initialized && (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      )}

      {selectedOfficeId && initialized && (error || !detail) && (
        <span className={style(tabStyles.errorMessage)}>{t('dashboard-office-detail-error-message')}</span>
      )}

      {selectedOfficeId && initialized && !error && detail && (
        <div>
          <div className={style(styles.detailGrid(isMobile))}>
            <div className={style(styles.officeGaugeCard)}>
              <div className={style(tabStyles.gaugeWrap)}>
                <Gauge
                  value={detail.office.occupancyPercentage}
                  thresholdMin={detail.office.thresholdMinPercentage}
                  thresholdMax={detail.office.thresholdMaxPercentage}
                  size={210}
                />
              </div>
              <div className={style(tabStyles.miniStats)}>
                <div className={style(tabStyles.miniStat)}>
                  <span className={style(tabStyles.miniStatValue)}>{detail.office.reserved}</span>
                  <span className={style(tabStyles.miniStatLabel)}>{t('dashboard-kpi-reserved')}</span>
                </div>
                <div className={style(tabStyles.miniStat)}>
                  <span className={style(tabStyles.miniStatValue)}>{detail.office.checkedIn}</span>
                  <span className={style(tabStyles.miniStatLabel)}>{t('dashboard-kpi-checked-in')}</span>
                </div>
                <div className={style(tabStyles.miniStat)}>
                  <span className={style(tabStyles.miniStatValue)}>{detail.office.effectiveCapacity}</span>
                  <span className={style(tabStyles.miniStatLabel)}>{t('dashboard-kpi-capacity')}</span>
                </div>
              </div>
            </div>

            {detail.lunchEnabled && (
              <div className={style(styles.lunchCard)}>
                <div className={style(styles.lunchValue)}>{detail.lunchCount}</div>
                <div className={style(styles.lunchDescription)}>
                  {t('dashboard-lunch-count-description', { count: detail.lunchCount, total: detail.office.checkedIn })}
                </div>
              </div>
            )}
          </div>

          <div className={style(styles.sectionCard)}>
            <div className={style(styles.sectionTitle)}>{t('dashboard-rooms-title')}</div>
            {detail.rooms.length === 0 && <span className={style(styles.emptyMessage)}>{t('dashboard-no-rooms-available')}</span>}
            {detail.rooms.length > 0 && (
              <div className={styles.tableWrap}>
                <table className={style(styles.table)}>
                  <thead>
                    <tr>
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-name')}
                        sortKeyName="name"
                        activeSortKey={roomsSortKey}
                        sortDirection={roomsSortDirection}
                        onSort={toggleRoomsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-morning')}
                        sortKeyName="morning"
                        activeSortKey={roomsSortKey}
                        sortDirection={roomsSortDirection}
                        onSort={toggleRoomsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-afternoon')}
                        sortKeyName="afternoon"
                        activeSortKey={roomsSortKey}
                        sortDirection={roomsSortDirection}
                        onSort={toggleRoomsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-kpi-checked-in')}
                        sortKeyName="checkedIn"
                        activeSortKey={roomsSortKey}
                        sortDirection={roomsSortDirection}
                        onSort={toggleRoomsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-threshold-label')}
                        sortKeyName="threshold"
                        activeSortKey={roomsSortKey}
                        sortDirection={roomsSortDirection}
                        onSort={toggleRoomsSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRooms.map((room: RoomOccupation) => (
                      <tr key={room.roomId}>
                        <td className={style(styles.td)}>
                          {room.name}
                          {room.hasOwnThresholds && (
                          <span className={style(styles.customBadge)}>{t('dashboard-custom-threshold-badge')}</span>
                          )}
                        </td>
                        <td className={style(styles.td)}>
                          <OccupancyBar
                            percentage={room.morning.occupancyPercentage}
                            min={room.thresholdMinPercentage}
                            max={room.thresholdMaxPercentage}
                          />
                        </td>
                        <td className={style(styles.td)}>
                          <OccupancyBar
                            percentage={room.afternoon.occupancyPercentage}
                            min={room.thresholdMinPercentage}
                            max={room.thresholdMaxPercentage}
                          />
                        </td>
                        <td className={style(styles.td)}>{room.checkedIn}</td>
                        <td className={style(styles.td)}>
                          {room.thresholdMinPercentage}–{room.thresholdMaxPercentage}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination page={roomsPage} pageCount={roomsPageCount} onChangePage={setRoomsPage} />
              </div>
            )}
          </div>

          <div className={style(styles.sectionCard)}>
            <div className={style(styles.sectionTitle)}>{t('dashboard-parkings-title')}</div>
            {detail.parkings.length === 0 && (
              <span className={style(styles.emptyMessage)}>{t('dashboard-no-parkings-available')}</span>
            )}
            {detail.parkings.length > 0 && (
              <div className={styles.tableWrap}>
                <table className={style(styles.table)}>
                  <thead>
                    <tr>
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-name')}
                        sortKeyName="name"
                        activeSortKey={parkingsSortKey}
                        sortDirection={parkingsSortDirection}
                        onSort={toggleParkingsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-morning')}
                        sortKeyName="morning"
                        activeSortKey={parkingsSortKey}
                        sortDirection={parkingsSortDirection}
                        onSort={toggleParkingsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-table-afternoon')}
                        sortKeyName="afternoon"
                        activeSortKey={parkingsSortKey}
                        sortDirection={parkingsSortDirection}
                        onSort={toggleParkingsSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.th)}
                        label={t('dashboard-threshold-label')}
                        sortKeyName="threshold"
                        activeSortKey={parkingsSortKey}
                        sortDirection={parkingsSortDirection}
                        onSort={toggleParkingsSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedParkings.map((parking: ParkingOccupation) => (
                      <tr key={parking.parkingId}>
                        <td className={style(styles.td)}>
                          {parking.name}
                          {parking.hasOwnThresholds && (
                          <span className={style(styles.customBadge)}>{t('dashboard-custom-threshold-badge')}</span>
                          )}
                        </td>
                        <td className={style(styles.td)}>
                          <OccupancyBar
                            percentage={parking.morning.occupancyPercentage}
                            min={parking.thresholdMinPercentage}
                            max={parking.thresholdMaxPercentage}
                          />
                        </td>
                        <td className={style(styles.td)}>
                          <OccupancyBar
                            percentage={parking.afternoon.occupancyPercentage}
                            min={parking.thresholdMinPercentage}
                            max={parking.thresholdMaxPercentage}
                          />
                        </td>
                        <td className={style(styles.td)}>
                          {parking.thresholdMinPercentage}–{parking.thresholdMaxPercentage}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination page={parkingsPage} pageCount={parkingsPageCount} onChangePage={setParkingsPage} />
              </div>
            )}
          </div>

          {detail.rooms.length > 0 && (
            <div className={style(styles.sectionCard)}>
              <div className={style(styles.sectionTitle)}>{t('dashboard-chart-occupancy-by-room')}</div>
              <Plot
                data={chartData}
                layout={{
                  autosize: true,
                  height: 360,
                  margin: { t: 10, r: 10, l: 40, b: 60 },
                  font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
                  yaxis: {
                    title: String(t('dashboard-chart-occupancy-axis')),
                    range: [0, 100],
                    gridcolor: DashboardTheme.gray100,
                    zeroline: false,
                  },
                  xaxis: { gridcolor: DashboardTheme.gray100, zeroline: false },
                  legend: { orientation: 'h' },
                  barmode: 'group',
                  plot_bgcolor: 'rgba(0,0,0,0)',
                  paper_bgcolor: 'rgba(0,0,0,0)',
                  hoverlabel: {
                    bgcolor: DashboardTheme.navy900,
                    bordercolor: DashboardTheme.navy900,
                    font: { color: DashboardTheme.white, family: DashboardTheme.bodyFontFamily, size: 12 },
                  },
                }}
                style={{ width: '100%' }}
                useResizeHandler
                config={plotlyChartConfig('occupancy-by-room')}
              />
              <div className={style(tabStyles.chartHint)}>
                <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
                {t('dashboard-chart-zoom-hint')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfficeDetailTab;
